import { Box, Button, Chip, CircularProgress, FormControl, Input, Option, Select, SvgIcon, Table, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ProposalDetailDialog from "../Dialogs/ProposalDetailDialog";

// Interface for proposal tracking records from the API
export interface ProposalTrackingRecord {
	BuildingName: string;
	InvoiceNumber: string;
	TotalAmount: number;
	InvoiceDate: Date;
	Description: string;
	Status: string;
	InvoiceHeaderId: string;
	IsApproved: boolean;
	DateMechanicSigned: Date;
	IsMechanicSigned: boolean;
	MechanicName: string;
	ActualInvoiceNumber: string;
	DateInvoiced: Date;
}

export default function ProposalTracking() {
	const [data, setData] = React.useState<Array<ProposalTrackingRecord>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [statusFilter, setStatusFilter] = React.useState("");
	const [order, setOrder] = React.useState(true);
	const [orderBy, setOrderBy] = React.useState(0);

	const [modal, setModal] = React.useState(false);
	const [selectedProposal, setSelectedProposal] = React.useState<ProposalTrackingRecord | null>(null);

	// Fetch proposal tracking data on mount and when status filter changes
	React.useEffect(() => {
		getData();
	}, [statusFilter]);

	// Fetch proposal tracking data from API
	const getData = () => {
		fetch("Proposal/GetProposalTracking", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify({
				statusFilter: statusFilter || null,
				searchTerm: null,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				// Map API response to ProposalTrackingRecord interface
				const res = x.map((y: any) => {
					return {
						BuildingName: y.building_name,
						InvoiceNumber: y.invoice_number,
						TotalAmount: y.total_amount,
						InvoiceDate: Util.ParseDate(y.invoice_date),
						Description: y.description,
						Status: y.status,
						InvoiceHeaderId: y.invoice_header_id,
						IsApproved: y.is_approved,
						DateMechanicSigned: Util.ParseDate(y.date_mechanic_signed),
						IsMechanicSigned: y.is_signed,
						MechanicName: y.mechanic_name,
						ActualInvoiceNumber: y.actual_invoice_number,
						DateInvoiced: Util.ParseDate(y.date_invoiced),
					} as ProposalTrackingRecord;
				});
				setData(res);
			});
	};

	// Format currency for display
	const formatCurrency = (amount: number) => {
		if (amount == null) return "";
		return "$" + amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	// Determine chip color based on status
	const getStatusColor = (status: string): "success" | "warning" | "neutral" | "primary" => {
		if (!status) return "neutral";
		const lowerStatus = status.toLowerCase();
		if (lowerStatus === "paid") return "success";
		if (lowerStatus === "approved") return "primary";
		if (lowerStatus === "pending") return "warning";
		return "neutral";
	};

	// Filter data based on global search
	const dt = (data ?? []).filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName?.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber?.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.Description?.toLowerCase().includes(globalFilter.toLowerCase())
	);

	const maxPages = Math.ceil(dt.length / 25);
	const cols = ["Proposal #", "Building", "Amount", "Date", "Description", "Status", "Mechanic", "Date Signed", "Invoice #", "Date Invoiced"];

	// Sort data based on selected column
	const old = new Date("1/1/1900");
	dt.sort((a, b) => {
		switch (cols[orderBy]) {
			case "Proposal #":
				return !order ? (a.InvoiceNumber ?? "").localeCompare(b.InvoiceNumber ?? "") : (b.InvoiceNumber ?? "").localeCompare(a.InvoiceNumber ?? "");
			case "Building":
				return !order ? (a.BuildingName ?? "").localeCompare(b.BuildingName ?? "") : (b.BuildingName ?? "").localeCompare(a.BuildingName ?? "");
			case "Amount":
				const aAmt = a.TotalAmount ?? 0;
				const bAmt = b.TotalAmount ?? 0;
				return !order ? aAmt - bAmt : bAmt - aAmt;
			case "Date":
				const ad = (a.InvoiceDate ?? old).getTime();
				const bd = (b.InvoiceDate ?? old).getTime();
				if ((ad > bd && !order) || (ad < bd && order)) return 1;
				if (ad === bd) return 0;
				return -1;
			case "Invoice #":
				return !order ? (a.ActualInvoiceNumber ?? "").localeCompare(b.ActualInvoiceNumber ?? "") : (b.ActualInvoiceNumber ?? "").localeCompare(a.ActualInvoiceNumber ?? "");
			case "Date Invoiced":
				const aa = (a.DateInvoiced ?? old).getTime();
				const bb = (b.DateInvoiced ?? old).getTime();
				if ((aa > bb && !order) || (aa < bb && order)) return 1;
				if (aa === bb) return 0;
				return -1;
			case "Description":
				return !order ? (a.Description ?? "").localeCompare(b.Description ?? "") : (b.Description ?? "").localeCompare(a.Description ?? "");
			case "Status":
				return !order ? (a.Status ?? "").localeCompare(b.Status ?? "") : (b.Status ?? "").localeCompare(a.Status ?? "");
		}
		return 0;
	});

	const thisData = dt.slice(pageNumber * 25, (pageNumber + 1) * 25);

	return (
		<>
			{/* Proposal Detail Dialog */}
			<ProposalDetailDialog
				open={modal}
				proposal={selectedProposal}
				close={() => {
					setModal(false);
					setSelectedProposal(null);
				}}
			/>
			<Box sx={{ overflowY: "auto", padding: 2 }}>
				<div>
					<Typography level="title-lg">Proposal Tracking</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
						{/* Status filter dropdown */}
						<Select
							placeholder="All Statuses"
							value={statusFilter}
							sx={{ minWidth: 150 }}
							onChange={(event, value) => setStatusFilter(value as string)}>
							<Option value="">All</Option>
							<Option value="Pending">Pending</Option>
							<Option value="Approved">Approved</Option>
							<Option value="Paid">Paid</Option>
						</Select>

						{/* Global search input */}
						<FormControl>
							<Input
								name="Search"
								placeholder="Search Invoice # or Building"
								onChange={(event) => {
									setGlobalFilter(event.target.value);
									setPageNumber(0);
								}}
								value={globalFilter}
								sx={{ minWidth: 250 }}
							/>
						</FormControl>
					</Box>
				</div>
				<Table stripe={"odd"} hoverRow>
					<thead>
						<tr>
							{cols.map((x, idx) => {
								return (
									<th
										key={idx}
										style={{ cursor: "pointer", width: idx === 4 ? "25%" : idx === 5 ? "10%" : "auto" }}
										onClick={() => {
											setOrderBy(idx);
											setOrder(orderBy === idx ? !order : true);
										}}>
										{x}
										{orderBy === idx ? (
											<SvgIcon onClick={() => setOrder(!order)} component={order ? ArrowDownwardIcon : ArrowUpwardIcon} sx={{ cursor: "pointer", marginTop: 1 }} />
										) : (
											<></>
										)}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{thisData?.map((x) => {
							return (
								<tr
									key={x.InvoiceHeaderId}
									onClick={() => {
										setModal(true);
										setSelectedProposal(x);
									}}
									style={{ cursor: "pointer" }}>
									<td>{x.InvoiceNumber}</td>
									<td>{x.BuildingName}</td>
									<td>{formatCurrency(x.TotalAmount)}</td>
									<td>{Util.Formatter(x.InvoiceDate, "D")}</td>
									<td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}>{x.Description}</td>
									<td>
										<Chip color={getStatusColor(x.Status)} size="sm">
											{x.Status}
										</Chip>
									</td>
									<td>{x.MechanicName}</td>
									<td>{Util.Formatter(x.DateMechanicSigned, "D")}</td>
									<td>{x.ActualInvoiceNumber}</td>
									<td>{Util.Formatter(x.DateInvoiced, "D")}</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={cols.length}>
								<div style={{ float: "right" }}>
									<span>
										<KeyboardArrowLeftIcon onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} />{" "}
										{`Page ${pageNumber + 1} of ${Math.max(1, maxPages)}`}{" "}
										<KeyboardArrowRightIcon onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} />
										<Typography level="body-sm" sx={{ display: "inline", ml: 2 }}>
											({dt.length} total)
										</Typography>
									</span>
								</div>
							</td>
						</tr>
					</tfoot>
				</Table>
			</Box>
		</>
	);
}
